import { createSelector } from '@reduxjs/toolkit';

import { ChannelSchema, PricingPlanFeature, PricingPlanSchema, UserSchema } from 'types';
import { EMPTY_USER } from 'utils';
import { RootState } from 'store/reducers';
import { MembershipChannelsType, ProfileState } from 'store/slices/profile';

export const profileStateSelector = (state: RootState): ProfileState => state.profile;

export const isOnFreeTrialSelector = createSelector<RootState, UserSchema, boolean>(
  state => profileUserSelector(state),
  profileUser => profileUser.onFreeTrial
);

export const hasAvailableMinutesSelector = createSelector<RootState, number, boolean>(
  state => availableMinutesSelector(state),
  availableMinutes => availableMinutes > 0
);

export const availableMinutesSelector = createSelector<RootState, UserSchema, number>(
  state => profileUserSelector(state),
  profileUser => profileUser.availableMinutes
);

export const currentPlanSelector = createSelector<RootState, UserSchema, PricingPlanSchema | null>(
  state => profileUserSelector(state),
  profileUser => profileUser.currentPlan
);

export const isOnPaidPlanSelector = createSelector<RootState, boolean, PricingPlanSchema | null, boolean>(
  state => isOnFreeTrialSelector(state),
  state => currentPlanSelector(state),
  (isOnFreeTrial, currentPlan) => !isOnFreeTrial && !!currentPlan?.price
);

export const isMembersOnlyAvailableSelector = createSelector<RootState, PricingPlanSchema | null, boolean>(
  state => currentPlanSelector(state),
  currentPlan => !!currentPlan && currentPlan.features.includes(PricingPlanFeature.MEMBERS_ONLY_EVENT)
);

export const isRecordingAvailableSelector = createSelector<RootState, PricingPlanSchema | null, boolean>(
  state => currentPlanSelector(state),
  currentPlan => !!currentPlan && currentPlan.features.includes(PricingPlanFeature.RECORD_VIDEO)
);

export const subscribedChannelsSelector = createSelector<RootState, ProfileState, ChannelSchema[]>(
  state => profileStateSelector(state),
  profileState => profileState.subscribedChannels || []
);

export const profileUserSelector = createSelector<RootState, ProfileState, UserSchema>(
  state => profileStateSelector(state),
  profileState => profileState.user || EMPTY_USER
);

export const hasProfileSelector = createSelector<RootState, UserSchema, boolean>(
  state => profileUserSelector(state),
  profileUser => !!profileUser.id
);

export const isSubscribedSelector = (channelName: string) =>
  createSelector<RootState, ChannelSchema[], boolean>(
    state => subscribedChannelsSelector(state),
    subscribedChannels => !!subscribedChannels.find(({ name }) => name === channelName)
  );

export const profileChannelNameSelector = createSelector<RootState, UserSchema, string>(
  state => profileUserSelector(state),
  user => user.channelName
);

export const hasChannelSelector = createSelector<RootState, string, boolean>(
  state => profileChannelIdSelector(state),
  profileChannelId => !!profileChannelId
);

export const profileChannelIdSelector = createSelector<RootState, UserSchema, string>(
  state => profileUserSelector(state),
  user => user.channelId
);

export const membershipChannelsSelector = createSelector<RootState, ProfileState, MembershipChannelsType>(
  state => profileStateSelector(state),
  profileState => profileState?.membershipChannels || []
);

export const isMemberSelector = (channelName: string) =>
  createSelector<RootState, MembershipChannelsType, boolean>(
    state => membershipChannelsSelector(state),
    membershipChannels => !!membershipChannels[channelName]
  );

export const membershipPricingPlanSelector = createSelector<RootState, ProfileState, PricingPlanSchema | null>(
  state => profileStateSelector(state),
  profileState => profileState?.membershipPricingPlan
);

export const hasMembershipPriceSelector = createSelector<RootState, PricingPlanSchema | null, boolean>(
  state => membershipPricingPlanSelector(state),
  membershipPricingPlan => !!membershipPricingPlan?.price
);
